import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useT } from '../../i18n';
import { L } from '../tokens';
import { Card, ReportPage } from './ReportParts';
import { postReport, uploadStatementFile } from './reportsApi';
import { countText } from './reportFormat';

// Step 1 of "Upload statement": choose a file, it is read on the server, and what the file shows (company, period) fills the form for the person to check. Step 2: "Compare with Floxir"
// opens the result. The file is never kept in the browser; the page only holds the answer's numbers and the statement's id (never in the address).
const fieldStyle = { width: '100%', boxSizing: 'border-box', height: 44, borderRadius: 10, border: `1px solid ${L.border}`, padding: '0 12px', font: 'inherit', fontSize: 16, background: '#FFFFFF', color: L.text, minWidth: 0 };
const buttonStyle = (primary, disabled) => ({
  height: 44, padding: '0 22px', borderRadius: 22, font: 'inherit', fontSize: 15, fontWeight: 600, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
  background: primary ? L.accent : '#FFFFFF', color: primary ? '#FFFFFF' : L.text, border: `1px solid ${primary ? L.accent : L.border}`,
});

// the month before `today` when the file shows no period
function previousMonth(today) {
  const [y, m] = String(today || '').split('-').map(Number);
  if (!y || !m) return { from: '', to: '' };
  const py = m === 1 ? y - 1 : y;
  const pm = m === 1 ? 12 : m - 1;
  const last = new Date(Date.UTC(py, pm, 0)).getUTCDate();
  const p = (n) => String(n).padStart(2, '0');
  return { from: `${py}-${p(pm)}-01`, to: `${py}-${p(pm)}-${p(last)}` };
}

const one = (t, base, n) => t(n === 1 ? `${base}.one` : `${base}.many`, { n: countText(n) });

export default function StatementUploadView() {
  const t = useT();
  const navigate = useNavigate();
  const input = useRef(null);
  const [phase, setPhase] = useState('pick');          // pick | reading | review | comparing
  const [error, setError] = useState(null);
  const [up, setUp] = useState(null);
  const [supplier, setSupplier] = useState('');
  const [period, setPeriod] = useState({ from: '', to: '' });
  const [prefilled, setPrefilled] = useState({ supplier: false, period: false });

  const errText = (code) => {
    const key = `reports.stmt.err.${code}`;
    const text = t(key);
    return text === key ? t('reports.stmt.err.generic') : text;
  };

  const choose = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (input.current) input.current.value = '';
    if (!file) return;
    setError(null); setPhase('reading');
    const out = await uploadStatementFile(file);
    if (!out.ok) { setError(out.error); setPhase('pick'); return; }
    const fallback = previousMonth(out.today);
    setUp(out);
    setSupplier(out.guess.supplier_id || '');
    setPeriod(out.guess.from && out.guess.to ? { from: out.guess.from, to: out.guess.to } : fallback);
    setPrefilled({ supplier: !!out.guess.supplier_id, period: !!(out.guess.from && out.guess.to) });
    setPhase('review');
  };

  const badPeriod = !!(period.from && period.to && period.from > period.to);
  const ready = phase === 'review' && supplier && period.from && period.to && !badPeriod;

  const compare = async () => {
    setError(null); setPhase('comparing');
    try {
      const res = await postReport('statement-compare', { id: up.id, supplier, from: period.from, to: period.to, currency: up.guess.currency || undefined });
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error('compare');
      navigate(`/reports/commissions/statement/${up.id}`, { replace: true });
    } catch {
      setError('compare'); setPhase('review');
    }
  };

  return (
    <ReportPage title={t('reports.stmt.upload.title')} backTo="/reports/commissions" testid="statement-upload">
      <Card>
        <p style={{ margin: '0 0 16px', fontSize: 16, color: L.muted, maxWidth: 720 }}>{t('reports.stmt.upload.intro')}</p>
        <input ref={input} type="file" accept=".pdf,.csv,.xlsx,application/pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={choose} data-testid="statement-file" style={{ display: 'none' }} aria-label={t('reports.stmt.upload.pick')} />
        {(phase === 'pick' || phase === 'reading') && (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14 }}>
            <button type="button" disabled={phase === 'reading'} onClick={() => input.current && input.current.click()} data-testid="statement-pick" style={buttonStyle(true, phase === 'reading')}>
              {phase === 'reading' ? t('reports.stmt.upload.reading') : t('reports.stmt.upload.pick')}
            </button>
            <span style={{ fontSize: 14, color: L.muted }}>{t('reports.stmt.upload.formats')}</span>
          </div>
        )}
        {error && <p role="alert" data-testid="statement-error" style={{ margin: '14px 0 0', color: L.accent, fontSize: 15 }}>{error === 'compare' ? t('reports.stmt.err.compare') : errText(error)}</p>}
      </Card>

      {(phase === 'review' || phase === 'comparing') && up && (
        <Card testid="statement-review">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', fontSize: 15, marginBottom: 6 }}>
            <strong style={{ overflowWrap: 'anywhere' }}>{up.stored.name}</strong>
            <span data-testid="statement-read-lines">{one(t, 'reports.stmt.upload.lines', up.read.lines)}</span>
            {up.read.unreadable > 0 && <span style={{ color: L.accent }}>{one(t, 'reports.stmt.upload.unreadable', up.read.unreadable)}</span>}
          </div>
          {up.read.from_image > 0 && <p data-testid="statement-image-note" style={{ margin: '0 0 12px', color: L.accent, fontSize: 14 }}>{t('reports.stmt.upload.fromImage', { n: countText(up.read.from_image) })}</p>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end', marginTop: 8 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: L.muted, flex: '1 1 260px', minWidth: 0 }}>{t('reports.stmt.upload.company')}
              <select value={supplier} onChange={(e) => { setSupplier(e.target.value); setPrefilled((p) => ({ ...p, supplier: false })); }} data-testid="statement-company" style={fieldStyle}>
                <option value="">{t('reports.stmt.upload.choose')}</option>
                {up.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: L.muted }}>{t('reports.stmt.upload.from')}
              <input type="date" value={period.from} onChange={(e) => { setPeriod({ ...period, from: e.target.value }); setPrefilled((p) => ({ ...p, period: false })); }} data-testid="statement-from" style={fieldStyle} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: L.muted }}>{t('reports.stmt.upload.to')}
              <input type="date" value={period.to} onChange={(e) => { setPeriod({ ...period, to: e.target.value }); setPrefilled((p) => ({ ...p, period: false })); }} data-testid="statement-to" style={fieldStyle} />
            </label>
          </div>
          {(prefilled.supplier || prefilled.period) && <p data-testid="statement-prefilled" style={{ margin: '10px 0 0', fontSize: 13, color: L.muted }}>{t('reports.stmt.upload.fromFile')}</p>}
          {badPeriod && <p role="alert" style={{ margin: '10px 0 0', fontSize: 14, color: L.accent }}>{t('reports.stmt.upload.badPeriod')}</p>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 18 }}>
            <button type="button" disabled={!ready} onClick={compare} data-testid="statement-compare" style={buttonStyle(true, !ready)}>
              {phase === 'comparing' ? t('reports.stmt.upload.comparing') : t('reports.stmt.upload.compare')}
            </button>
            <button type="button" disabled={phase === 'comparing'} onClick={() => { setPhase('pick'); setUp(null); setError(null); }} data-testid="statement-another" style={buttonStyle(false, phase === 'comparing')}>{t('reports.stmt.upload.another')}</button>
          </div>
        </Card>
      )}
    </ReportPage>
  );
}
